import React, { useRef, useEffect, useCallback } from 'react';
import styles from './ScrollPicker.module.css';

/**
 * 수평 스크롤 피커 컴포넌트
 * 터치 스와이프로 값을 선택할 수 있는 iOS 스타일 피커
 * 
 * @param {Array} items - 선택 가능한 아이템 배열 [{value, label, subLabel?}]
 * @param {any} selectedValue - 현재 선택된 값
 * @param {Function} onChange - 값 변경 시 콜백
 * @param {boolean} isMonth - 월 피커 여부 (더 좁은 아이템)
 */
const ScrollPicker = ({ items, selectedValue, onChange, isMonth = false }) => {
    const containerRef = useRef(null);
    const ignoreScrollSelectionRef = useRef(false);
    const userScrollIntentRef = useRef(false);
    const scrollUnlockTimeoutRef = useRef(null);
    const scrollEndTimeoutRef = useRef(null);
    const alignRetryTimeoutRef = useRef(null);

    const scheduleScrollUnlock = useCallback((delay = 80) => {
        window.clearTimeout(scrollUnlockTimeoutRef.current);
        scrollUnlockTimeoutRef.current = window.setTimeout(() => {
            ignoreScrollSelectionRef.current = false;
        }, delay);
    }, []);

    const findClosestIndex = useCallback(() => {
        if (!containerRef.current) return -1;
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;

        let closestIndex = -1;
        let closestDistance = Infinity;

        Array.from(container.children).forEach((item, idx) => {
            const itemRect = item.getBoundingClientRect();
            const itemCenter = itemRect.left + itemRect.width / 2;
            const distance = Math.abs(containerCenter - itemCenter);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = idx;
            }
        });

        return closestIndex;
    }, []);

    const alignToIndex = useCallback((index, behavior = 'smooth') => {
        if (!containerRef.current) return;
        const container = containerRef.current;
        const children = container.children;
        if (index < 0 || index >= children.length) return;

        const item = children[index];
        const scrollPos = Math.max(0, item.offsetLeft - (container.clientWidth / 2) + (item.offsetWidth / 2));

        item.scrollIntoView({ inline: 'center', block: 'nearest', behavior });
        container.scrollTo({ left: scrollPos, behavior });
    }, []);

    // 해당 값을 중앙으로 스크롤
    const scrollToCenter = useCallback((index, { behavior = 'smooth' } = {}) => {
        ignoreScrollSelectionRef.current = true;
        userScrollIntentRef.current = false;
        scheduleScrollUnlock(behavior === 'smooth' ? 420 : 220);
        window.clearTimeout(alignRetryTimeoutRef.current);

        alignToIndex(index, behavior);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                alignToIndex(index, 'auto');
            });
        });

        alignRetryTimeoutRef.current = window.setTimeout(() => {
            alignToIndex(index, 'auto');
        }, behavior === 'smooth' ? 240 : 140);
    }, [alignToIndex, scheduleScrollUnlock]);

    const markUserScrollIntent = useCallback(() => {
        userScrollIntentRef.current = true;
        window.clearTimeout(scrollEndTimeoutRef.current);
    }, []);

    // 스크롤 시 중앙 아이템 감지
    const handleScroll = useCallback(() => {
        if (!containerRef.current || ignoreScrollSelectionRef.current || !userScrollIntentRef.current) return;

        window.clearTimeout(scrollEndTimeoutRef.current);
        scrollEndTimeoutRef.current = window.setTimeout(() => {
            const closestIndex = findClosestIndex();
            userScrollIntentRef.current = false;

            const newValue = items[closestIndex]?.value;
            if (newValue !== undefined && newValue !== selectedValue) {
                onChange(newValue);
            }
        }, 120);
    }, [findClosestIndex, items, selectedValue, onChange]);

    // 마운트 시 선택된 값으로 스크롤
    useEffect(() => {
        const timer = setTimeout(() => {
            const index = items.findIndex(item => item.value === selectedValue);
            if (index !== -1) {
                scrollToCenter(index, { behavior: 'auto' });
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [items, scrollToCenter, selectedValue]);

    useEffect(() => {
        if (!containerRef.current || typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(() => {
            const index = items.findIndex(item => item.value === selectedValue);
            if (index !== -1) {
                scrollToCenter(index, { behavior: 'auto' });
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [items, scrollToCenter, selectedValue]);

    useEffect(() => {
        return () => {
            window.clearTimeout(scrollUnlockTimeoutRef.current);
            window.clearTimeout(scrollEndTimeoutRef.current);
            window.clearTimeout(alignRetryTimeoutRef.current);
        };
    }, []);

    // 아이템 클릭 핸들러
    const handleItemClick = (index, value) => {
        onChange(value);
        scrollToCenter(index);
    };

    return (
        <div className={styles.scrollPickerWrapper}>
            <div
                ref={containerRef}
                className={`${styles.scrollPicker} ${isMonth ? styles.scrollPickerMonth : ''}`}
                onScroll={handleScroll}
                onPointerDown={markUserScrollIntent}
                onTouchStart={markUserScrollIntent}
                onWheel={markUserScrollIntent}
            >
                {items.map((item, index) => (
                    <div
                        key={item.value}
                        className={`${styles.scrollPickerItem} ${selectedValue === item.value ? styles.scrollPickerItemActive : ''}`}
                        onClick={() => handleItemClick(index, item.value)}
                    >
                        {item.label}
                        {item.subLabel && (
                            <div className={styles.scrollPickerRevenue}>
                                {item.subLabel}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ScrollPicker;
